import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { LANGUAGE_STORAGE_KEY, SUPPORTED_LANGUAGES } from "../i18n";

/**
 * useLanguageSync
 *
 * Bridges the authenticated user's `language` field (server-side, per-account
 * preference) with i18next / localStorage on the client.
 *
 * Behaviour:
 *   - On login (user object appears) → adopt the user's server-stored language
 *     if it exists AND differs from the current language, then mirror it to
 *     localStorage so the next visit is instant.
 *   - On logout (user disappears) → keep the current language, do nothing.
 *   - Guests: no server call.  i18next-browser-languagedetector already
 *     handles localStorage / navigator / htmlTag.
 *
 * NEVER downgrade an explicit guest choice (localStorage already set) unless
 * the user's account explicitly stores a value.
 */
export default function useLanguageSync() {
  const { user } = useAuth();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (!user) return;

    const serverLang = (user.language || "").split("-")[0];
    if (!serverLang || !SUPPORTED_LANGUAGES.includes(serverLang)) return;

    const current = (i18n.language || "").split("-")[0];
    if (serverLang === current) return;

    i18n.changeLanguage(serverLang);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, serverLang);
    } catch (_e) {
      // ignore
    }
  }, [user, i18n]);
}
